/**
 * Analytics Tracker for Music Listening
 * Automatically tracks listening behavior and sends to analytics API
 */

import { trackSongPlay, trackSongComplete } from './analytics';
import type { Song } from '../types';

interface ListeningSession {
  id: string;
  startTime: number;
  currentSong?: Song;
  songsPlayed: number;
  songsCompleted: number;
  totalDuration: number;
  deviceType: string;
}

interface StreamTrackingData {
  songId: string;
  startTime: number;
  duration: number;
  quality: string;
  completed: boolean;
}

class AnalyticsTracker {
  private currentSession: ListeningSession | null = null;
  private currentStream: StreamTrackingData | null = null;
  private apiEndpoint = '/api/analytics';
  private isTracking = true;

  constructor() {
    this.initializeDeviceType();
  }

  private initializeDeviceType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('mobile')) return 'mobile';
    if (userAgent.includes('tablet')) return 'tablet';
    return 'desktop';
  }

  // Start a new listening session
  startSession(): void {
    if (!this.isTracking) return;

    this.currentSession = {
      id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      startTime: Date.now(),
      songsPlayed: 0,
      songsCompleted: 0,
      totalDuration: 0,
      deviceType: this.initializeDeviceType()
    };

    console.log('Analytics: Started listening session', this.currentSession.id);
  }

  // End the current listening session
  async endSession(): Promise<void> {
    if (!this.isTracking || !this.currentSession) return;

    try {
      // End any current stream
      if (this.currentStream) {
        await this.endStream(false);
      }

      // Send session data to API
      await this.sendSessionUpdate(this.currentSession, true);
      
      console.log('Analytics: Ended listening session', this.currentSession.id);
      this.currentSession = null;
    } catch (error) {
      console.error('Error ending analytics session:', error);
    }
  }

  // Start tracking a new song
  startSongTracking(song: Song, quality: string = 'standard'): void {
    if (!this.isTracking) return;

    // End previous stream if exists
    if (this.currentStream) {
      this.endStream(false);
    }

    // Start new session if none exists
    if (!this.currentSession) {
      this.startSession();
    }

    this.currentStream = {
      songId: song.id,
      startTime: Date.now(),
      duration: 0,
      quality,
      completed: false
    };

    // Update session
    if (this.currentSession) {
      this.currentSession.currentSong = song;
      this.currentSession.songsPlayed++;
    }

    // Track song play event
    trackSongPlay(song.id, song.artistId || 'unknown', 'player');
    console.log('Analytics: Started tracking song', song.title);
  }

  // Update listening progress
  updateProgress(currentTime: number, duration: number): void {
    if (!this.isTracking || !this.currentStream || !this.currentSession) return;

    this.currentStream.duration = currentTime;
    this.currentSession.totalDuration = Date.now() - this.currentSession.startTime;

    // Mark as completed if listened to 30+ seconds or 50%+ of song
    const completionThreshold = Math.min(30, duration * 0.5);
    if (currentTime >= completionThreshold && !this.currentStream.completed) {
      this.currentStream.completed = true;
      this.currentSession.songsCompleted++;
      
      // Track completion
      if (this.currentSession.currentSong) {
        const completionRate = duration > 0 ? currentTime / duration : 0;
        trackSongComplete(
          this.currentSession.currentSong.id,
          currentTime,
          completionRate
        );
      }
    }
  }

  // End current stream tracking
  async endStream(completed?: boolean): Promise<void> {
    if (!this.isTracking || !this.currentStream) return;

    try {
      // Override completion status if provided
      if (completed !== undefined) {
        this.currentStream.completed = completed;
        if (completed && this.currentSession) {
          this.currentSession.songsCompleted++;
        }
      }

      // Send stream data to API
      await this.sendStreamData(this.currentStream);
      
      console.log('Analytics: Ended stream tracking', this.currentStream.songId);
      this.currentStream = null;
    } catch (error) {
      console.error('Error ending stream tracking:', error);
    }
  }

  // Send stream data to analytics API
  private async sendStreamData(stream: StreamTrackingData): Promise<void> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${this.apiEndpoint}/track-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          songId: stream.songId,
          duration: Math.round(stream.duration),
          quality: stream.quality,
          deviceType: this.currentSession?.deviceType,
          userAgent: navigator.userAgent,
          completed: stream.completed
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to track stream: ${response.status}`);
      }
    } catch (error) {
      console.error('Error sending stream data:', error);
    }
  }

  // Send session update to analytics API
  private async sendSessionUpdate(session: ListeningSession, isEnd: boolean = false): Promise<void> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${this.apiEndpoint}/update-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId: isEnd ? session.id : undefined,
          songsPlayed: session.songsPlayed,
          songsCompleted: session.songsCompleted,
          totalDuration: Math.round(session.totalDuration / 1000), // Convert to seconds
          deviceType: session.deviceType
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update session: ${response.status}`);
      }
    } catch (error) {
      console.error('Error sending session update:', error);
    }
  }

  // Get current session info
  getCurrentSession(): ListeningSession | null {
    return this.currentSession;
  }

  // Enable/disable tracking
  setTracking(enabled: boolean): void {
    this.isTracking = enabled;
    if (!enabled && this.currentSession) {
      this.endSession();
    }
  }

  // Check if tracking is enabled
  isTrackingEnabled(): boolean {
    return this.isTracking;
  }
}

// Export singleton instance
export const analyticsTracker = new AnalyticsTracker();

// Auto-start session when page loads
if (typeof window !== 'undefined') {
  // Start session on page load
  window.addEventListener('load', () => {
    analyticsTracker.startSession();
  });

  // End session when page unloads
  window.addEventListener('beforeunload', () => {
    analyticsTracker.endSession();
  });

  // Handle page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      analyticsTracker.endSession();
    } else if (document.visibilityState === 'visible') {
      analyticsTracker.startSession();
    }
  });
} 